import { z } from 'zod';

/** One field-level change for an "UPDATED" activity (shown behind "View changes"). The before/after
 *  values are `dynamic` on the backend (string, number, bool, …) — accept any type and stringify in
 *  the mapper, so a non-string value never fails the whole activity-list parse. */
const ActivityChangeSchema = z.object({
  field_name: z.string().nullish(),
  before_value: z.unknown(),
  after_value: z.unknown(),
});

/**
 * One activity-feed entry from `GET /api/v1/user-activity`. Only the fields the table consumes are
 * typed; `meta.title_lang` carries the interpolation values for `title_lang_path`'s `:placeholders`.
 */
const UserActivityDtoSchema = z.object({
  _id: z.string(),
  title_lang_path: z.string().nullish(),
  action_type: z.string().nullish(),
  // Which module the activity belongs to (e.g. "groups", "expenses", "wallet_deposit"). Gates the
  // "View changes" button (mobile parity). Absent on some feeds → the mapper treats it leniently.
  reference_module: z.string().nullish(),
  changes: z.array(ActivityChangeSchema).nullish(),
  created_at: z.string().nullish(),
  group_name: z.string().nullish(),
  performed_by_user_name: z.string().nullish(),
  meta: z
    .object({
      title_lang: z.record(z.string(), z.union([z.string(), z.number()])).nullish(),
    })
    .nullish(),
});

/** Envelope: `{ status, message, data: { activities } }` (nullish-tolerant on `data`). */
export const UserActivityListResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: z.object({ activities: z.array(UserActivityDtoSchema).default([]) }).nullish(),
});

export type UserActivityDto = z.infer<typeof UserActivityDtoSchema>;
